import axios from "axios";
import { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { Container } from "reactstrap";
import styled from "styled-components";

import NewFooter from "../../components/Footers/NewFooter";
import NewNavbar from "../../components/Navbar/NewNavbar";
import { API_URL } from "../../constants";
import { track } from "../../utils/analytics";
import { authHeader, clearToken, getToken } from "../../utils/auth";
import { storageSet } from "../../utils/storage";
import {
  findPropertyByListing,
  formatDate,
  groupByProperty,
  hasUnsharedEdits,
  isPollingStatus,
  isSupportOnlyStatus,
  primaryActionFor,
  propertyStatusMeta,
  seedsListing,
  supportNoticeFor,
} from "../DashboardPage/propertyFeed";
import PropertyGlance from "./PropertyGlance";
import { Card, CardLabel, HintText, QuietAction } from "./styled";

/** @typedef {import("../DashboardPage/propertyFeed").ReportSummary} ReportSummary */

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #eef5f4 0%, #f9fafb 340px);
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 120px 0 70px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  text-decoration: none;

  &:hover {
    color: #0e7e7a;
    text-decoration: none;
  }
`;

/** The property's identity block: hero, address, status, updated. */
const Hero = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  margin: 10px 0 24px;
`;

const HeroImage = styled.img`
  width: 112px;
  height: 112px;
  border-radius: 14px;
  object-fit: cover;
  flex-shrink: 0;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;

  @media (max-width: 540px) {
    width: 84px;
    height: 84px;
  }
`;

const HeroPlaceholder = styled.div`
  width: 112px;
  height: 112px;
  border-radius: 14px;
  flex-shrink: 0;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.2rem;

  @media (max-width: 540px) {
    width: 84px;
    height: 84px;
  }
`;

const Address = styled.h1`
  font-size: 1.6rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 6px;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const StatusBadge = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 4px 12px;
  border-radius: 999px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
`;

const MetaLine = styled.div`
  font-size: 0.85rem;
  color: #6b7280;
  margin-top: 6px;
`;

const UnsharedNote = styled.span`
  color: #92400e;
  font-weight: 600;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

const PrimaryAction = styled.a`
  font-size: 0.9rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 10px 20px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid #0e7e7a;
  background: #0e7e7a;
  color: #ffffff;

  &:hover {
    background: #0b605d;
    color: #ffffff;
  }
`;

const PreparingChip = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #92400e;
  display: inline-flex;
  align-items: center;
  min-height: 44px;
`;

const HistoryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid #f0f0f0;
  flex-wrap: wrap;

  &:first-of-type {
    border-top: none;
    padding-top: 0;
  }
`;

const HistoryMeta = styled.div`
  min-width: 0;

  strong {
    font-size: 0.95rem;
    color: #1a1a1a;
  }
`;

const Centered = styled.div`
  text-align: center;
  padding: 80px 0;
  color: #4a4a4a;

  h2 {
    font-size: 1.4rem;
    color: #1a1a1a;
    margin-bottom: 8px;
  }

  a {
    color: #0e7e7a;
    font-weight: 600;
  }
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/** One prep row in the version history: date, status, one link.
 * @param {{ prep: ReportSummary, isLatest: boolean }} props
 */
const HistoryEntry = ({ prep, isLatest }) => {
  const meta = propertyStatusMeta(prep.status);
  const unavailable = prep.status === "preparing" || isSupportOnlyStatus(prep.status);
  const href = unavailable
    ? null
    : prep.editable
      ? `/reports/${prep.computation_uuid}/edit`
      : prep.report_pdf_url || null;
  const external = !prep.editable && !!href;
  return (
    <HistoryRow>
      <HistoryMeta>
        <strong>{formatDate(prep.created_at)}</strong>{" "}
        <StatusBadge $bg={meta.$bg} $fg={meta.$fg}>
          {isLatest ? `${meta.label} · current` : meta.label}
        </StatusBadge>
      </HistoryMeta>
      {href && (
        <QuietAction
          href={href}
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        >
          {prep.editable ? "Open" : "View PDF"}
        </QuietAction>
      )}
    </HistoryRow>
  );
};

/** A property's space: its listing prep, the assets underneath, version
 * history, and property-level actions — the durable object the dashboard's
 * cards open into. Routed by any of the property's listing uuids. */
const PropertyPage = () => {
  const { listingUuid } = /** @type {{ listingUuid: string }} */ (useParams());
  const history = useHistory();
  const [reports, setReports] = useState(/** @type {ReportSummary[]} */ ([]));
  const [status, setStatus] = useState(/** @type {"loading" | "ok" | "error"} */ ("loading"));

  useEffect(() => {
    if (!getToken()) {
      history.push("/login");
      return;
    }
    axios
      .get(`${API_URL}/auth/me/reports/`, { headers: authHeader() })
      .then((resp) => {
        setReports(resp.data.reports || []);
        setStatus("ok");
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          clearToken();
          history.push("/login");
          return;
        }
        console.error("Property load failed:", err);
        setStatus("error");
      });
  }, [history]);

  const property = findPropertyByListing(groupByProperty(reports), listingUuid);
  const shouldPoll = status === "ok" && isPollingStatus(property?.latest.status);

  // The at-a-glance section reads the listing's document once it is
  // delivered and editable (owner reads only). A miss leaves the section out
  // — the rest of the page never depends on it.
  const delivered =
    !!property &&
    property.latest.editable &&
    !isPollingStatus(property.latest.status) &&
    !isSupportOnlyStatus(property.latest.status);
  const glanceUuid = delivered ? property.latest.computation_uuid : null;
  const [glanceDoc, setGlanceDoc] = useState(
    /** @type {{ uuid: string, doc: Record<string, any> } | null} */ (null)
  );
  useEffect(() => {
    if (!glanceUuid) return undefined;
    let cancelled = false;
    axios
      .get(`${API_URL}/computations/${glanceUuid}/report-document/`, {
        headers: authHeader(),
      })
      .then((resp) => {
        if (!cancelled && resp?.data) setGlanceDoc({ uuid: glanceUuid, doc: resp.data });
      })
      .catch(() => {
        // Leave the section out; the feed row still renders the page.
      });
    return () => {
      cancelled = true;
    };
  }, [glanceUuid]);

  // Keep the 8s clock stable while the property remains in a transitional
  // server state; a same-status refresh must not restart the interval.
  useEffect(() => {
    if (!shouldPoll) return undefined;
    const handle = window.setInterval(() => {
      axios
        .get(`${API_URL}/auth/me/reports/`, { headers: authHeader() })
        .then((resp) => setReports(resp.data.reports || []))
        .catch((err) => {
          if (err.response?.status === 401) {
            clearToken();
            history.push("/login");
          }
          // Other transient failures retain the current property until the next tick.
        });
    }, 8000);
    return () => window.clearInterval(handle);
  }, [history, shouldPoll]);

  let body;
  if (status === "loading") {
    body = (
      <Centered>
        <Spinner />
        <p>Loading this property…</p>
      </Centered>
    );
  } else if (status === "error") {
    body = (
      <Centered>
        <h2>Something went wrong</h2>
        <p>
          We couldn't load this property. Try refreshing, or email{" "}
          <a href="mailto:help@listino.ai">help@listino.ai</a>.
        </p>
      </Centered>
    );
  } else {
    if (!property) {
      body = (
        <Centered>
          <h2>Property not found</h2>
          <p>
            It may belong to another account. <Link to="/dashboard">Back to your listings</Link>.
          </p>
        </Centered>
      );
    } else {
      const { latest, earlier } = property;
      const meta = propertyStatusMeta(latest.status);
      const primary = primaryActionFor(latest);
      const externalProps = primary.external
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {};
      const editUrl = `/reports/${latest.computation_uuid}/edit`;
      const shareUrl = `/reports/${latest.computation_uuid}/share`;
      const updatedIso = latest.edited_at || latest.shared_at || latest.created_at;
      const preparing = latest.status === "preparing";
      const supportNotice = supportNoticeFor(latest.status);
      const trackOpen = () => {
        // "Finish preparing" / "Try again" land on the
        // confirmation page, which reads the listing from session storage —
        // seed it here too, or the page opens empty.
        if (seedsListing(primary) && latest.listing_uuid) {
          storageSet("currentListingId", latest.listing_uuid);
        }
        track("prep_property_opened", { status: latest.status || "ready" });
      };
      body = (
        <>
          <Hero>
            {latest.hero_image_url ? (
              <HeroImage src={latest.hero_image_url} alt="" />
            ) : (
              <HeroPlaceholder aria-hidden="true">🏡</HeroPlaceholder>
            )}
            <div>
              <Address>{latest.address || "Your listing"}</Address>
              <StatusBadge $bg={meta.$bg} $fg={meta.$fg}>
                {meta.label}
              </StatusBadge>
              <MetaLine>
                Updated {formatDate(updatedIso)}
                {hasUnsharedEdits(latest) && <UnsharedNote> · unshared edits</UnsharedNote>}
              </MetaLine>
            </div>
          </Hero>

          {glanceDoc && glanceDoc.uuid === latest.computation_uuid && (
            <PropertyGlance uuid={glanceDoc.uuid} doc={glanceDoc.doc} latest={latest} />
          )}

          <Card>
            <CardLabel>Listing prep</CardLabel>
            <ActionRow>
              {preparing ? (
                <PreparingChip>Preparing your listing prep…</PreparingChip>
              ) : supportNotice ? (
                // A failed credits run keeps a self-service way back in
                // (primaryActionFor); everything else support-only stays so.
                primary.href ? (
                  <PrimaryAction href={primary.href} onClick={trackOpen}>
                    {primary.label}
                  </PrimaryAction>
                ) : (
                  <QuietAction href="mailto:help@listino.ai">Contact support</QuietAction>
                )
              ) : (
                <>
                  {primary.href && (
                    <PrimaryAction href={primary.href} {...externalProps} onClick={trackOpen}>
                      {primary.label}
                    </PrimaryAction>
                  )}
                  {latest.editable && primary.label !== "Personalize" && (
                    <QuietAction href={editUrl}>Personalize</QuietAction>
                  )}
                  {latest.editable && primary.label !== "Share" && (
                    <QuietAction href={shareUrl}>Share</QuietAction>
                  )}
                </>
              )}
            </ActionRow>
            {preparing && (
              <HintText>
                Preparation continues in the background if you leave this page. This listing's
                status updates automatically here; review and sharing options appear when the
                analysis is available.
              </HintText>
            )}
            {supportNotice && <HintText>{supportNotice}</HintText>}
            {!preparing && !supportNotice && latest.editable && (
              <HintText>
                Share holds the listing package — the web share link and every file you've added to
                this listing.
              </HintText>
            )}
          </Card>

          {!preparing && !supportNotice && (latest.report_pdf_url || latest.touchup_url) && (
            <Card>
              <CardLabel>Assets</CardLabel>
              <ActionRow>
                {latest.report_pdf_url && (
                  <QuietAction
                    href={latest.report_pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View PDF
                  </QuietAction>
                )}
                {latest.editable && (
                  <QuietAction href={shareUrl}>Download the listing package</QuietAction>
                )}
                {latest.touchup_url && (
                  <QuietAction href={latest.touchup_url}>Touched-up photos</QuietAction>
                )}
              </ActionRow>
            </Card>
          )}

          <Card>
            <CardLabel>Version history</CardLabel>
            <HistoryEntry prep={latest} isLatest />
            {earlier.map((prep) => (
              <HistoryEntry key={prep.computation_uuid} prep={prep} isLatest={false} />
            ))}
          </Card>
        </>
      );
    }
  }

  return (
    <PageContainer>
      <NewNavbar />
      <ContentWrapper>
        <Container style={{ maxWidth: 860 }}>
          <BackLink to="/dashboard">← Your listings</BackLink>
          {body}
        </Container>
      </ContentWrapper>
      <NewFooter />
    </PageContainer>
  );
};

export default PropertyPage;
